import React, { useRef, useCallback, useEffect, useState } from 'react';
import NoteToolbar from './NoteToolbar';
import TableContextMenu from './editor/TableContextMenu';
import { validateFile, readAsDataUrl, uploadViaBlob } from './editor/ImageUploadButton';

function sanitizeEditorHtml(html = '') {
    return String(html)
        .replace(/<gwm\b[^>]*>[\s\S]*?<\/gwm>/gi, '')
        .replace(/<grammarly-extension\b[^>]*>[\s\S]*?<\/grammarly-extension>/gi, '')
        .replace(/<grammarly-desktop-integration\b[^>]*>[\s\S]*?<\/grammarly-desktop-integration>/gi, '')
        .replace(/\u200B/g, '')
        .trim();
}

/**
 * NoteEditor — Rich text editor using contentEditable.
 *
 * Stores content as { html, plainText } so we can persist the structured
 * HTML to MongoDB and extract plainText for search indexing.
 *
 * Supports:
 *   • Text formatting (Bold, Italic, H1, etc.)
 *   • Image upload (toolbar, clipboard paste, drag-drop)
 *   • Table creation & inline editing
 *
 * Props:
 *   value       — { html: string, plainText: string }  (initial)
 *   onChange     — ({ html, plainText }) => void
 *   readOnly?   — boolean
 *   placeholder? — string
 *   autoFocus?  — boolean
 *   companyId?  — for image uploads
 *   tabId?      — for image uploads
 */
export default function NoteEditor({
    value,
    onChange,
    readOnly = false,
    placeholder = 'Start writing your research notes...',
    autoFocus = false,
    companyId,
    tabId,
}) {
    const editorRef = useRef(null);
    const containerRef = useRef(null);
    const uploadQueueRef = useRef([]);
    const uploadingRef = useRef(false);
    const resizeStateRef = useRef(null);
    const movingNodeRef = useRef(null);
    const [isEmpty, setIsEmpty] = useState(true);
    const [dragOver, setDragOver] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    const [resizeBox, setResizeBox] = useState(null);
    const [resizeDialog, setResizeDialog] = useState({ open: false, width: 0, height: 0, lockAspect: true, originalW: 0, originalH: 0 });
    const initializedRef = useRef(false); // Track if we've done initial render

    const clearImageSelection = useCallback(() => {
        setSelectedImage(null);
        setResizeBox(null);
    }, []);

    const updateResizeBox = useCallback(() => {
        if (!selectedImage || !containerRef.current || !editorRef.current?.contains(selectedImage)) {
            setResizeBox(null);
            return;
        }
        const imageRect = selectedImage.getBoundingClientRect();
        const containerRect = containerRef.current.getBoundingClientRect();
        const scrollLeft = containerRef.current.scrollLeft || 0;
        const scrollTop = containerRef.current.scrollTop || 0;
        setResizeBox({
            left: imageRect.left - containerRect.left + scrollLeft,
            top: imageRect.top - containerRect.top + scrollTop,
            width: imageRect.width,
            height: imageRect.height,
        });
    }, [selectedImage]);

    const markMovableNodes = useCallback(() => {
        if (!editorRef.current) return;
        const nodes = editorRef.current.querySelectorAll('img, table');
        nodes.forEach((node) => {
            node.setAttribute('draggable', 'true');
            node.classList.add('notes-movable-node');
        });
    }, []);

    // Initialize editor HTML when value changes
    useEffect(() => {
        if (!editorRef.current) {
            console.log('[NoteEditor] editorRef not ready');
            return;
        }

        const newHtml = sanitizeEditorHtml(value?.html || '');
        const currentHtml = editorRef.current.innerHTML;

        // Log more details on first initialization
        if (!initializedRef.current) {
            console.log('[NoteEditor] First initialization:', {
                hasNewContent: !!newHtml,
                newLength: newHtml.length,
                preview: newHtml.slice(0, 200) + (newHtml.length > 200 ? '...' : '')
            });
            initializedRef.current = true;
        } else {
            console.log('[NoteEditor] Update:', {
                newLength: newHtml.length,
                currentLength: currentHtml.length
            });
        }

        // Always update innerHTML when value changes to ensure rendering
        if (currentHtml !== newHtml) {
            console.log('[NoteEditor] ✅ Updating innerHTML');
            editorRef.current.innerHTML = newHtml;
        } else {
            console.log('[NoteEditor] Content unchanged, skipping innerHTML update');
        }

        markMovableNodes();

        // ALWAYS update isEmpty state to ensure placeholder doesn't cover content
        const renderedText = (editorRef.current.innerText || '').trim();
        const renderedHtml = (editorRef.current.innerHTML || '').trim();
        const hasContent = !!renderedText || (renderedHtml && renderedHtml !== '<br>');
        setIsEmpty(!hasContent);
        if (hasContent) {
            console.log('[NoteEditor] ✅ Content visible, hiding placeholder');
        } else {
            console.log('[NoteEditor] ⚠️ Content still empty after render');
        }
    }, [value, markMovableNodes]);

    // Auto-focus
    useEffect(() => {
        if (autoFocus && editorRef.current && !readOnly) {
            setTimeout(() => editorRef.current?.focus(), 100);
        }
    }, [autoFocus, readOnly]);

    useEffect(() => {
        updateResizeBox();
    }, [updateResizeBox, value]);

    useEffect(() => {
        if (!selectedImage) return;
        const onWindowResize = () => updateResizeBox();
        window.addEventListener('resize', onWindowResize);
        window.addEventListener('scroll', onWindowResize, true);
        return () => {
            window.removeEventListener('resize', onWindowResize);
            window.removeEventListener('scroll', onWindowResize, true);
        };
    }, [selectedImage, updateResizeBox]);

    const handleInput = useCallback(() => {
        if (!editorRef.current) return;
        const rawHtml = editorRef.current.innerHTML;
        const html = sanitizeEditorHtml(rawHtml);

        const temp = document.createElement('div');
        temp.innerHTML = html;
        const plainText = (temp.innerText || '').trim();

        const previousHtml = sanitizeEditorHtml(value?.html || '');
        const previousPlainText = (value?.plainText || '').trim();
        if (html === previousHtml && plainText === previousPlainText) {
            return;
        }

        const hasMeaningfulHtml = !!html && html !== '<br>';
        setIsEmpty(!plainText && !hasMeaningfulHtml);
        onChange?.({ html, plainText });
        markMovableNodes();
        setTimeout(() => updateResizeBox(), 0);
    }, [onChange, value, updateResizeBox, markMovableNodes]);

    const insertUploadPlaceholder = useCallback((label = 'Uploading screenshot...') => {
        const id = `upload-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        editorRef.current?.focus();
        document.execCommand(
            'insertHTML',
            false,
            `<span class="notes-upload-placeholder" data-upload-id="${id}">${label}</span>`
        );
        return id;
    }, []);

    const replaceUploadPlaceholder = useCallback((placeholderId, html) => {
        if (!editorRef.current || !placeholderId) return;
        const node = editorRef.current.querySelector(`[data-upload-id="${placeholderId}"]`);
        if (!node) return;
        node.outerHTML = html;
    }, []);

    const removeUploadPlaceholder = useCallback((placeholderId) => {
        if (!editorRef.current || !placeholderId) return;
        const node = editorRef.current.querySelector(`[data-upload-id="${placeholderId}"]`);
        if (node) node.remove();
    }, []);

    const uploadSingleImage = useCallback(async (file, placeholderId = null) => {
        const err = validateFile(file);
        if (err) {
            if (placeholderId) replaceUploadPlaceholder(placeholderId, `<span class="notes-upload-placeholder notes-upload-placeholder-error">${err}</span>`);
            return;
        }

        const editor = editorRef.current;
        if (!editor) return;
        editor.focus();

        try {
            const { blobUrl, attachmentId } = await uploadViaBlob(file, companyId, tabId, () => { });
            const imgHtml = `<img src="${blobUrl}" alt="${file.name}" data-attachment-id="${attachmentId}" style="max-width:100%;height:auto;display:block;margin:12px 0;border-radius:6px;" loading="lazy" />`;
            if (placeholderId) replaceUploadPlaceholder(placeholderId, imgHtml);
            else document.execCommand('insertHTML', false, imgHtml);
        } catch {
            try {
                const dataUrl = await readAsDataUrl(file);
                const imgHtml = `<img src="${dataUrl}" alt="${file.name}" style="max-width:100%;height:auto;display:block;margin:12px 0;border-radius:6px;" loading="lazy" />`;
                if (placeholderId) replaceUploadPlaceholder(placeholderId, imgHtml);
                else document.execCommand('insertHTML', false, imgHtml);
            } catch {
                if (placeholderId) removeUploadPlaceholder(placeholderId);
            }
        }
        handleInput();
    }, [companyId, tabId, handleInput, replaceUploadPlaceholder, removeUploadPlaceholder]);

    const processUploadQueue = useCallback(async () => {
        if (uploadingRef.current) return;
        uploadingRef.current = true;
        while (uploadQueueRef.current.length > 0) {
            const nextItem = uploadQueueRef.current.shift();
            await uploadSingleImage(nextItem.file, nextItem.placeholderId);
        }
        uploadingRef.current = false;
    }, [uploadSingleImage]);

    const enqueueImageUpload = useCallback((file, placeholderLabel = 'Uploading screenshot...') => {
        const placeholderId = insertUploadPlaceholder(placeholderLabel);
        uploadQueueRef.current.push({ file, placeholderId });
        processUploadQueue();
    }, [insertUploadPlaceholder, processUploadQueue]);

    const handleKeyDown = useCallback((e) => {
        const isCmd = e.ctrlKey || e.metaKey;
        const key = e.key.toLowerCase();

        // Ctrl/Cmd + S → trigger manual save (bubble event)
        if (isCmd && key === 's') {
            e.preventDefault();
            editorRef.current?.dispatchEvent(new CustomEvent('note-save', { bubbles: true }));
            return;
        }

        if (selectedImage && (key === 'delete' || key === 'backspace')) {
            e.preventDefault();
            selectedImage.remove();
            clearImageSelection();
            handleInput();
            return;
        }

        if (isCmd && key === 'l') { e.preventDefault(); document.execCommand('justifyLeft', false, null); return; }
        if (isCmd && key === 'e') { e.preventDefault(); document.execCommand('justifyCenter', false, null); return; }
        if (isCmd && key === 'r') { e.preventDefault(); document.execCommand('justifyRight', false, null); return; }
        if (isCmd && key === 'j') { e.preventDefault(); document.execCommand('justifyFull', false, null); return; }

        if (isCmd && key === '\\') { e.preventDefault(); document.execCommand('removeFormat', false, null); handleInput(); return; }
        if (isCmd && !e.shiftKey && e.key === '=') { e.preventDefault(); document.execCommand('subscript', false, null); return; }
        if (isCmd && e.shiftKey && e.key === '+') { e.preventDefault(); document.execCommand('superscript', false, null); return; }

        if (isCmd && e.shiftKey && (e.key === '>' || e.key === '<')) {
            e.preventDefault();
            const selection = window.getSelection();
            if (selection && selection.rangeCount > 0 && !selection.isCollapsed) {
                const range = selection.getRangeAt(0);
                const node = selection.focusNode?.nodeType === Node.TEXT_NODE ? selection.focusNode.parentElement : selection.focusNode;
                const currentSize = parseInt(window.getComputedStyle(node).fontSize, 10) || 14;
                const nextSize = e.key === '>' ? Math.min(currentSize + 2, 96) : Math.max(currentSize - 2, 8);
                const span = document.createElement('span');
                span.style.fontSize = `${nextSize}px`;
                span.appendChild(range.extractContents());
                range.insertNode(span);
                handleInput();
            }
            return;
        }

        // Tab/Shift+Tab → indent/outdent
        if (e.key === 'Tab') {
            e.preventDefault();
            document.execCommand(e.shiftKey ? 'outdent' : 'indent', false, null);
        }
    }, [selectedImage, clearImageSelection, handleInput]);

    const handlePaste = useCallback((e) => {
        /* ── Image paste (screenshots, copied images) ── */
        const items = Array.from(e.clipboardData?.items || []);
        const imageItem = items.find((i) => i.type.startsWith('image/'));
        if (imageItem) {
            e.preventDefault();
            const file = imageItem.getAsFile();
            if (file) enqueueImageUpload(file, 'Uploading image...');
            return;
        }

        /* ── HTML / text paste (strip scripts, styles) ── */
        e.preventDefault();
        const html = e.clipboardData.getData('text/html');
        const text = e.clipboardData.getData('text/plain');
        if (html) {
            const clean = html
                .replace(/<script[\s\S]*?<\/script>/gi, '')
                .replace(/<style[\s\S]*?<\/style>/gi, '')
                .replace(/\son\w+="[^"]*"/gi, '');
            document.execCommand('insertHTML', false, clean);
        } else {
            document.execCommand('insertText', false, text);
        }
    }, [enqueueImageUpload]);

    /* ── Drag & Drop ── */
    const handleDragOver = useCallback((e) => {
        if (readOnly) return;
        const hasFiles = Array.from(e.dataTransfer?.types || []).includes('Files');
        if (hasFiles) { e.preventDefault(); setDragOver(true); }
    }, [readOnly]);

    const handleDragLeave = useCallback(() => setDragOver(false), []);

    const handleDrop = useCallback((e) => {
        setDragOver(false);
        if (readOnly) return;
        const files = Array.from(e.dataTransfer?.files || []);
        const imageFile = files.find((f) => f.type.startsWith('image/'));
        if (imageFile) {
            e.preventDefault();
            e.stopPropagation();
            enqueueImageUpload(imageFile, 'Uploading image...');
        }
    }, [readOnly, enqueueImageUpload]);

    const handleEditorClick = useCallback((e) => {
        const target = e.target;
        if (target instanceof HTMLImageElement) {
            setSelectedImage(target);
            setTimeout(() => updateResizeBox(), 0);
            return;
        }
        clearImageSelection();
    }, [clearImageSelection, updateResizeBox]);

    const handleEditorDoubleClick = useCallback((e) => {
        const target = e.target;
        if (!(target instanceof HTMLImageElement)) return;
        const width = Math.round(target.getBoundingClientRect().width);
        const height = Math.round(target.getBoundingClientRect().height);
        setSelectedImage(target);
        setResizeDialog({
            open: true,
            width,
            height,
            lockAspect: true,
            originalW: target.naturalWidth || width,
            originalH: target.naturalHeight || height,
        });
    }, []);

    const handleEditorDragStart = useCallback((e) => {
        const target = e.target;
        if (!(target instanceof HTMLElement)) return;
        const moveTarget = target.closest('img, table');
        if (!(moveTarget instanceof HTMLElement)) return;
        movingNodeRef.current = moveTarget;
        moveTarget.classList.add('notes-moving-node');
        if (e.dataTransfer) {
            e.dataTransfer.effectAllowed = 'move';
            e.dataTransfer.setData('text/plain', 'finlytics-editor-node-move');
        }
    }, []);

    const handleEditorDragOver = useCallback((e) => {
        if (!movingNodeRef.current) return;
        e.preventDefault();
        if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
    }, []);

    const handleEditorDrop = useCallback((e) => {
        if (!movingNodeRef.current || readOnly) return;
        e.preventDefault();
        e.stopPropagation();

        const movingNode = movingNodeRef.current;
        const editor = editorRef.current;
        if (!editor || !editor.contains(movingNode)) return;

        let range = null;
        if (document.caretRangeFromPoint) {
            range = document.caretRangeFromPoint(e.clientX, e.clientY);
        } else if (document.caretPositionFromPoint) {
            const pos = document.caretPositionFromPoint(e.clientX, e.clientY);
            if (pos) {
                range = document.createRange();
                range.setStart(pos.offsetNode, pos.offset);
                range.collapse(true);
            }
        }

        if (range && !movingNode.contains(range.startContainer)) {
            range.collapse(true);
            range.insertNode(movingNode);
        } else {
            editor.appendChild(movingNode);
        }

        movingNode.classList.remove('notes-moving-node');
        movingNodeRef.current = null;
        markMovableNodes();
        handleInput();
        setTimeout(() => updateResizeBox(), 0);
    }, [readOnly, handleInput, markMovableNodes, updateResizeBox]);

    const handleEditorDragEnd = useCallback(() => {
        if (movingNodeRef.current) {
            movingNodeRef.current.classList.remove('notes-moving-node');
            movingNodeRef.current = null;
        }
    }, []);

    const startImageResize = useCallback((e, handle) => {
        if (!selectedImage) return;
        e.preventDefault();
        e.stopPropagation();
        const rect = selectedImage.getBoundingClientRect();
        resizeStateRef.current = {
            handle,
            startX: e.clientX,
            startY: e.clientY,
            startW: rect.width,
            startH: rect.height,
            aspect: rect.width / Math.max(rect.height, 1),
        };
    }, [selectedImage]);

    useEffect(() => {
        const onMouseMove = (e) => {
            const state = resizeStateRef.current;
            if (!state || !selectedImage) return;

            const dx = e.clientX - state.startX;
            const dy = e.clientY - state.startY;
            let nextW = state.startW;
            let nextH = state.startH;

            const unlockRatio = e.shiftKey;
            const horizontal = state.handle.includes('e') || state.handle.includes('w');
            const vertical = state.handle.includes('n') || state.handle.includes('s');

            if (horizontal) {
                nextW = state.startW + (state.handle.includes('w') ? -dx : dx);
            }
            if (vertical) {
                nextH = state.startH + (state.handle.includes('n') ? -dy : dy);
            }

            if (!unlockRatio && state.handle.length === 2) {
                const delta = Math.max(Math.abs(dx), Math.abs(dy));
                const signed = (state.handle.includes('w') || state.handle.includes('n')) ? -delta : delta;
                nextW = state.startW + signed;
                nextH = nextW / state.aspect;
            }

            nextW = Math.max(60, Math.round(nextW));
            nextH = Math.max(40, Math.round(nextH));

            selectedImage.style.width = `${nextW}px`;
            selectedImage.style.height = `${nextH}px`;
            selectedImage.style.maxWidth = 'none';
            updateResizeBox();
        };

        const onMouseUp = () => {
            if (!resizeStateRef.current) return;
            resizeStateRef.current = null;
            handleInput();
        };

        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('mouseup', onMouseUp);
        return () => {
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
        };
    }, [selectedImage, handleInput, updateResizeBox]);

    const applyResizeDialog = useCallback(() => {
        if (!selectedImage) return;
        const width = Math.max(60, Number(resizeDialog.width) || 60);
        const height = Math.max(40, Number(resizeDialog.height) || 40);
        selectedImage.style.width = `${width}px`;
        selectedImage.style.height = `${height}px`;
        selectedImage.style.maxWidth = 'none';
        setResizeDialog((prev) => ({ ...prev, open: false }));
        updateResizeBox();
        handleInput();
    }, [selectedImage, resizeDialog, updateResizeBox, handleInput]);

    const resetImageSize = useCallback(() => {
        if (!selectedImage) return;
        selectedImage.style.width = '';
        selectedImage.style.height = '';
        selectedImage.style.maxWidth = '100%';
        setResizeDialog((prev) => ({
            ...prev,
            width: prev.originalW,
            height: prev.originalH,
        }));
        updateResizeBox();
        handleInput();
    }, [selectedImage, updateResizeBox, handleInput]);

    /* ── Table context menu content-change callback ── */
    const handleTableChange = useCallback(() => {
        handleInput();
    }, [handleInput]);

    return (
        <div className="notes-editor-wrapper">
            {!readOnly && (
                <NoteToolbar
                    editorRef={editorRef}
                    companyId={companyId}
                    tabId={tabId}
                    onContentChange={handleInput}
                />
            )}
            <div
                ref={containerRef}
                className={`notes-editor-container ${dragOver ? 'notes-editor-dragover' : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
            >
                {isEmpty && !readOnly && (
                    <div className="notes-editor-placeholder">{placeholder}</div>
                )}
                {dragOver && (
                    <div className="notes-editor-drop-overlay">
                        <span>📤 Drop image here to upload</span>
                    </div>
                )}
                <div
                    ref={editorRef}
                    className="notes-editor notes-editor-content"
                    contentEditable={!readOnly}
                    suppressContentEditableWarning
                    onInput={handleInput}
                    onKeyDown={handleKeyDown}
                    onPaste={handlePaste}
                    onClick={handleEditorClick}
                    onDoubleClick={handleEditorDoubleClick}
                    onDragStart={handleEditorDragStart}
                    onDragOver={handleEditorDragOver}
                    onDrop={handleEditorDrop}
                    onDragEnd={handleEditorDragEnd}
                    spellCheck
                    role="textbox"
                    aria-multiline="true"
                    aria-label="Note editor"
                />

                {resizeBox && selectedImage && !readOnly && (
                    <div
                        className="notes-image-resize-box"
                        style={{
                            left: `${resizeBox.left}px`,
                            top: `${resizeBox.top}px`,
                            width: `${resizeBox.width}px`,
                            height: `${resizeBox.height}px`,
                        }}
                    >
                        {['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'].map((handle) => (
                            <button
                                key={handle}
                                type="button"
                                className={`notes-image-resize-handle notes-image-resize-handle-${handle}`}
                                onMouseDown={(e) => startImageResize(e, handle)}
                            />
                        ))}
                    </div>
                )}

                {resizeDialog.open && selectedImage && !readOnly && (
                    <div className="notes-image-size-dialog" onMouseDown={(e) => e.stopPropagation()}>
                        <div className="notes-image-size-title">Resize Image</div>
                        <label>
                            Width
                            <input
                                type="number"
                                min="60"
                                value={resizeDialog.width}
                                onChange={(e) => {
                                    const width = Number(e.target.value) || 60;
                                    if (resizeDialog.lockAspect) {
                                        const height = Math.round(width * (resizeDialog.originalH / Math.max(resizeDialog.originalW, 1)));
                                        setResizeDialog((prev) => ({ ...prev, width, height }));
                                    } else {
                                        setResizeDialog((prev) => ({ ...prev, width }));
                                    }
                                }}
                            />
                        </label>
                        <label>
                            Height
                            <input
                                type="number"
                                min="40"
                                value={resizeDialog.height}
                                onChange={(e) => {
                                    const height = Number(e.target.value) || 40;
                                    if (resizeDialog.lockAspect) {
                                        const width = Math.round(height * (resizeDialog.originalW / Math.max(resizeDialog.originalH, 1)));
                                        setResizeDialog((prev) => ({ ...prev, width, height }));
                                    } else {
                                        setResizeDialog((prev) => ({ ...prev, height }));
                                    }
                                }}
                            />
                        </label>
                        <label className="notes-image-size-lock">
                            <input
                                type="checkbox"
                                checked={resizeDialog.lockAspect}
                                onChange={(e) => setResizeDialog((prev) => ({ ...prev, lockAspect: e.target.checked }))}
                            />
                            Lock aspect ratio
                        </label>
                        <div className="notes-image-size-actions">
                            <button type="button" onClick={resetImageSize}>Reset</button>
                            <button type="button" onClick={applyResizeDialog}>Apply</button>
                            <button type="button" onClick={() => setResizeDialog((prev) => ({ ...prev, open: false }))}>Cancel</button>
                        </div>
                    </div>
                )}
            </div>
            {/* Table right-click context menu */}
            {!readOnly && (
                <TableContextMenu
                    editorRef={editorRef}
                    onContentChange={handleTableChange}
                />
            )}
        </div>
    );
}
